import type {Flow} from "flow-to-typescript-codemod";

import Log, {Errors} from "@khan/logging";

import type {getFullUserProfile_user_profile as UserProfile} from "#data-access/__graphql-types__/getFullUserProfile.ts";

/**
 * Code to handle the public components of a profile.
 *
 * Profile information about a user.
 * May be complete, partially filled, or mostly empty depending on the
 * permissions the current user has to this profile.
 */
import Backbone, {
    type Backbone_Attrs,
    type ModelOpts,
} from "./backbone-lite.js";
import {AffiliationModel} from "./eduorg-models.ts";

export type BadgeCounts = [number, number, number, number, number, number];

export type ProfileAccessLevel = "COACH" | "PUBLIC";

// This model has a lot of fields in it, some of which are used in
// only limited contexts.  It's expensive to fetch them all, so we
// have a long-term goal of moving fields out of here and onto their
// use-locations.  (As a graphql query.)  To aid with that, the fields
// below are organized by where they're used.
// TODO(csilvers): document what web-pages use each field, not just js files.
// TODO(FEI-3298): Update `ProfileModelType` to consume fields directly from the
// gqlflow generated `getFullUserProfile`.
export interface ProfileModelType extends Backbone.Model {
    // valid getters

    // -- HOMEPAGE FIELDS -- used only on the logged-in homepgae.

    // apps/khanacademy/src/app-shell-package/homepage-route.jsx
    get(arg1: "homepageUrl"): string;
    // -- EXERCISE FIELDS -- exercise and test-prep-exercise packages.

    // apps/khanacademy/src/exercises-package/stateful-exercise-manager.jsx
    get(arg1: "hideVisual"): boolean;
    get(arg1: "isDeveloper"): boolean;
    // apps/khanacademy/src/perseus-one-package/application-state-store.jsx
    get(arg1: "isPublisher"): boolean;
    // -- PROGRAM FIELDS -- scratchpads-package

    // apps/khanacademy/src/scratchpads-package/scratchpad-about.jsx
    get(arg1: "isPublic"): boolean;
    // -- CONTENT FIELDS -- used by multiple/most content pages.

    // apps/khanacademy/src/content-library-legacy/components/mastery-accelerant-modal/helpers.js
    // apps/khanacademy/src/content-library-legacy/components/mastery-accelerant-modal/mastery-accelerant-modal-launcher.jsx
    // apps/khanacademy/src/content-library-legacy/components/mastery-accelerant-modal/mastery-accelerant-modal-launcher.jsx
    // apps/khanacademy/src/content-library-legacy/modules/courses-dashboard/topic-container.jsx
    get(arg1: "hasCoach"): boolean;
    // apps/khanacademy/src/assignments-async-loader-package/async-create-assignment-header.jsx
    // apps/khanacademy/src/components/modal-package/article-modal.jsx
    // apps/khanacademy/src/components/modal-package/exercise-modal.jsx
    // apps/khanacademy/src/components/modal-package/video-modal.jsx
    // apps/khanacademy/src/video-package/video-player/video-player.jsx
    get(arg1: "hasClasses"): boolean;
    // -- DISCUSSION FIELDS -- used on discussion pages, including programs,
    //                         and sometimes in profile too.

    // apps/khanacademy/src/logged-in-homepage-package/content-pages/discussion.jsx
    // apps/khanacademy/src/profile-package/profile-discussion.js
    get(arg1: "canHellban"): boolean;
    // apps/khanacademy/src/logged-in-homepage-package/content-pages/discussion.jsx
    // apps/khanacademy/src/profile-package/profile-discussion.js
    get(arg1: "canMessageUsers"): boolean;
    // -- PROFILE FIELDS -- used only in profile packages.
    // TODO(csilvers): some of these are for settings, some for
    // the actual /profile url, and some for the hompage.  Split it up!

    // apps/khanacademy/src/profile-settings-package/profile-settings.jsx
    // @ts-expect-error - TS2749 - 'AffiliationModel' refers to a value, but is being used as a type here. Did you mean 'typeof AffiliationModel'?
    get(arg1: "affiliation"): AffiliationModel;
    // apps/khanacademy/src/profile-nav-package/user-points-and-badges.jsx
    get(arg1: "badgeCounts"): BadgeCounts;
    // apps/khanacademy/src/profile-package/profile-widgets.jsx
    get(arg1: "countVideosCompleted"): number;
    get(arg1: "dateJoined"): string;
    // apps/khanacademy/src/profile-nav-package/user-card-view.jsx
    get(arg1: "includesUserDataInfo"): boolean;
    get(arg1: "isDataCollectible"): boolean; // via isFullyEditable()
    // apps/khanacademy/src/settings-package/public-profile-info-section.jsx
    get(arg1: "profile"): UserProfile | null | undefined;
    // apps/khanacademy/src/profile-nav-package/user-card-view.jsx
    // apps/khanacademy/src/profile-settings-package/profile-settings.jsx
    // apps/khanacademy/src/settings-package/public-profile-info-section.jsx
    get(arg1: "bio"): string;
    // apps/khanacademy/src/data-access/profile-utils.js
    // apps/khanacademy/src/donate-package/methods/paypal.js
    // apps/khanacademy/src/profile-package/profile-widgets.jsx
    get(arg1: "userId"): string;
    // -- USER SETTINGS FIELDS -- used for user settings and preferences
    get(arg1: "isInKhanClassroomDistrict"): boolean;
    // apps/khanacademy/src/discussion-package/util.js
    // apps/khanacademy/src/notifications-package/readable-notifications/avatar-notification.jsx
    // apps/khanacademy/src/profile-nav-package/user-points-and-badges.jsx
    // apps/khanacademy/src/profile-package/profile-widgets.jsx
    get(arg1: "points"): number;
    // apps/khanacademy/src/profile-package/coaches.js
    get(arg1: "isCoachingLoggedInUser"): boolean;
    // apps/khanacademy/src/page-package/maybe-show-donate-banner.jsx
    get(arg1: "joined"): string;
    // These are used in the profile dropdown.
    // apps/khanacademy/src/page-package/util.js
    get(arg1: "isMidsignupPhantom"): boolean;
    // -- LOGIN/SIGNUP FIELDS -- used only on login

    // apps/khanacademy/src/auth/pages/join/join-class-page.jsx (for /join)
    get(arg1: "canModifyCoaches"): boolean;
    // -- UNSURE YET HOW WIDELY THESE ARE NEEDED

    // apps/khanacademy/src/page-package/util.js
    get(arg1: "hasStudents"): boolean;
    // apps/khanacademy/src/page-package/util.js
    get(arg1: "canAccessDistrictsHomepage"): boolean;
    // apps/khanacademy/src/app-shell-package/login-required-route.jsx
    // apps/khanacademy/src/clarifications-package/discussion-meta.jsx
    // apps/khanacademy/src/discussion-package/components/discussion-list.jsx
    // apps/khanacademy/src/discussion-package/util.js
    // apps/khanacademy/src/profile-package/profile-widgets.jsx
    get(arg1: "isChildAccount"): boolean;
    // apps/khanacademy/src/clarifications-package/discussion-meta.jsx
    // apps/khanacademy/src/discussion-package/components/discussion-input.jsx
    // apps/khanacademy/src/discussion-package/components/discussion-list.jsx
    // apps/khanacademy/src/discussion-package/components/discussion-post-accepted-clarification.jsx
    // apps/khanacademy/src/discussion-package/util.js
    // apps/khanacademy/src/scratchpads-package/scratchpad-about.jsx
    // apps/khanacademy/src/video-package/video-player/video-player.jsx
    get(arg1: "isModerator"): boolean;
    // apps/khanacademy/src/logged-in-homepage-package/content-pages/profile-util.js
    // apps/khanacademy/src/logged-in-homepage-package/logged-in-homepage.jsx
    // apps/khanacademy/src/page-package/util.js
    // apps/khanacademy/src/profile-nav-package/user-points-and-badges.jsx
    // apps/khanacademy/src/scratchpads-package/scratchpad-about.jsx
    // apps/khanacademy/src/scratchpads-package/scratchpad-spinoffs.jsx
    get(arg1: "profileRoot"): string;
    // apps/khanacademy/src/discussion-package/components/discussion-avatar.jsx
    // apps/khanacademy/src/logged-in-homepage-package/content-pages/profile-util.js
    // apps/khanacademy/src/profile-nav-package/profile.js
    // ...and indirectly, via usernameFormatted:
    // apps/khanacademy/src/profile-nav-package/user-card-view.jsx
    // apps/khanacademy/src/scratchpads-package/testdata/childsprogram_testdata.js
    // apps/khanacademy/src/scratchpads-package/testdata/project_testdata.js
    // apps/khanacademy/src/scratchpads-package/testdata/userprogram_testdata.js
    // apps/khanacademy/src/scratchpads-package/testdata/usersprogram_testdata.js
    // apps/khanacademy/src/shared-package/profile-model.js
    get(arg1: "username"): string;
    // apps/khanacademy/src/logged-in-homepage-package/content-pages/discussion.jsx
    // ... but also more places via isEditable and isFullyEditable.
    get(arg1: "isSelf"): boolean;
    // apps/khanacademy/src/data-access/profile-utils.js
    // ... basically any page that has animations
    get(arg1: "prefersReducedMotion"): boolean;
    // -- NEEDED ON EVERY PAGE (e.g. for analytics, header, etc.)

    // libs/analytics/performance/src/google-analytics.js
    // apps/khanacademy/src/page-package/util.js
    get(arg1: "isParent"): boolean;
    // libs/analytics/performance/src/google-analytics.js
    // apps/khanacademy/src/page-package/util.js
    // apps/khanacademy/src/content-library-legacy/modules/courses-dashboard/topic-container.jsx
    get(arg1: "isTeacher"): boolean;
    // libs/analytics/performance/src/google-analytics.js
    get(arg1: "gaUserId"): string | undefined; // phantom users don't get a gaUserId
    // apps/khanacademy/src/page-package/util.js
    get(arg1: "hasChildStudents"): boolean;
    // apps/khanacademy/src/notifications-package/with-notifications.jsx
    get(arg1: "countBrandNewNotifications"): number;
    // apps/khanacademy/src/site-banners/site-banner-config.js
    // ?KAGraphQLDateTime
    get(arg1: "underAgeGate"):
        | {
              parentEmail: string;
              daysUntilCutoff: number;
              approvalGivenAt: string | null | undefined;
          }
        | null
        | undefined;
    // apps/khanacademy/src/site-banners/verify-email-utils.js
    get(arg1: "authEmails"): Array<string>;
    // apps/khanacademy/src/site-banners/verify-email-utils.js
    get(arg1: "pendingEmailVerifications"): Array<{
        email: string;
    }>;
    // apps/khanacademy/src/site-banners/verify-email-utils.js
    get(arg1: "signupDataIfUnverified"):
        | {
              email: string;
              emailBounced: boolean;
          }
        | null
        | undefined;
    // apps/khanacademy/src/site-banners/site-banner-config.js
    get(arg1: "tosAccepted"): boolean;
    // apps/khanacademy/src/site-banners/site-banner-config.js
    get(arg1: "actorIsImpersonatingUser"): boolean;
    // apps/khanacademy/src/site-banners/site-banner-config.js
    get(arg1: "shouldShowAgeCheck"): boolean;
    // apps/khanacademy/src/site-banners/site-banner-config.js
    get(arg1: "includesDistrictOwnedData"): boolean;
    // apps/khanacademy/src/page-package/site-header.tsx
    get(arg1: "includesKmapDistrictOwnedData"): boolean;
    // apps/khanacademy/src/page-package/site-header.tsx
    get(arg1: "includesK4dDistrictOwnedData"): boolean;
    // apps/khanacademy/src/mastery-tower-package/use-sound-effects.js
    get(arg1: "soundOn"): boolean;
    // apps/khanacademy/src/guide/guide-access-gate.jsx
    get(arg1: "isAIGuideEnabled"): boolean;
    // apps/khanacademy/src/guide/guide-access-gate.tsx
    get(arg1: "hasAccessToAIGuideCompanionMode"): boolean;
    // apps/khanacademy/src/guide-activities/ui/activities-list/use-activity-groups-and-welcome.ts
    // apps/khanacademy/src/guide-activities/ui/activities-redirect.tsx
    // apps/khanacademy/src/page-package/khanmigo-activities-dropdown.tsx
    get(arg1: "hasAccessToAIGuideLearner"): boolean;
    get(arg1: "hasAccessToAIGuideDev"): boolean;
    // apps/khanacademy/src/guide-activities/ui/activities-list/use-activity-groups-and-welcome.ts
    get(arg1: "hasAccessToAIGuideDistrictAdmin"): boolean;
    // apps/khanacademy/src/guide-activities/ui/activities-list/use-activity-groups-and-welcome.ts
    // apps/khanacademy/src/guide-activities/ui/activities-redirect.tsx
    // apps/khanacademy/src/page-package/khanmigo-activities-dropdown.tsx
    // apps/khanacademy/src/guide/hooks/use-guide-terms-messages.ts
    // apps/khanacademy/src/guide/internal/get-default-persona-for-profile.ts
    // apps/khanacademy/src/guide/internal/maybe-get-as-student-persona.ts
    // apps/khanacademy/src/guide/ui/header/use-props-for-header-type.ts
    // apps/khanacademy/src/guide/ui/slate/contexts.ts
    get(arg1: "hasAccessToAIGuideParent"): boolean;
    get(arg1: "hasAccessToAIGuideTeacher"): boolean;
    // apps/khanacademy/src/regional-standards-package/use-user-settings-region.ts
    get(arg1: "region"): string | null | undefined;
    // apps/khanacademy/src/regional-standards-package/use-user-settings-region.ts
    get(arg1: "schoolAffiliation"):
        | {
              id: string;
              location: string;
          }
        | null
        | undefined;
    // apps/khanacademy/src/regional-standards-package/use-user-settings-region.ts
    get(arg1: "userDistrictInfos"): Array<{
        id: string;
        isKAD: boolean;
        district: {
            id: string;
            region: string;
        };
    }>;
    // apps/khanacademy/src/guide-activities/ui/activities-redirect.tsx
    // apps/khanacademy/src/guide-activities/ui/activities-list/use-activity-groups-and-welcome.tsx
    get(arg1: "isK4dStudent"): boolean;
    get(arg1: "isKmapStudent"): boolean;

    // -- USED IN TOO MANY PLACES TO MENTION

    get(arg1: "kaid"): string;
    get(arg1: "nickname"): string;
    get(arg1: "email"): string;
    get(arg1: "isPhantom"): boolean;
    get(arg1: "birthMonthYear"): string | null | undefined;
    // -- SPECIAL CASES

    // custom methods based on get()
    usernameFormatted(): string;
    isEditable(): boolean;
    isFullyEditable(): boolean;
    isSal(): boolean;
    // other custom methods
    validateNickname(nickname: string): void;
}

const ProfileModel: Flow.Class<ProfileModelType> = Backbone.Model.extend(
    {
        defaults: {
            affiliation: null,
            avatar: {
                imageSrc: "/images/avatars/svg/blobby-green.svg",
                thumbnailSrc: "/images/avatars/svg/blobby-green.svg",
                imagePath: "/images/avatars/svg/blobby-green.svg",
            },
            badgeCounts: {
                "0": 0,
                "1": 0,
                "2": 0,
                "3": 0,
                "4": 0,
                "5": 0,
            },
            bio: "",
            profile: null,
            countVideosCompleted: 0,
            dateJoined: "",
            email: "",
            isCoachingLoggedInUser: false,
            nickname: "",
            points: 0,
            profileRoot: "",
            username: "",
            isDataCollectible: false,
            isSelf: false,
            isPublic: false,
            isPublisher: false,
            canModifyCoaches: true,
            hideVisual: false,
            tosAccepted: true,
        },

        initialize: function (
            this: any,
            attributes: Backbone_Attrs,
            options: ModelOpts,
        ) {
            Backbone.Model.prototype.initialize.call(this, attributes, options);

            this._ensureAttrsAreSubmodels();

            // Any time the submodel attributes are set(), we need to make sure
            // they're still models of the right type.
            // @ts-expect-error - TS2339 - Property 'SUBMODEL_ATTRS' does not exist on type 'Class<ProfileModelType>'.
            const eventStr = ProfileModel.SUBMODEL_ATTRS.map(
                (attr) => "change:" + attr,
            ).join(" ");
            this.on(eventStr, this._ensureAttrsAreSubmodels, this);
        },

        usernameFormatted: function (this: any) {
            const username = this.get("username");
            return username ? "@" + username : "";
        },

        /**
         * Instantiate Backbone models for complex attributes that have
         * dedicated models and set() them on this ProfileModel.
         *
         * Will only reassign attributes that aren't already assigned to
         * Backbone models of the right type.
         */
        _ensureAttrsAreSubmodels: function (this: any) {
            const attrs: Record<string, any> = {};

            const affiliation =
                this.get("affiliation") || new AffiliationModel();

            attrs.affiliation =
                affiliation instanceof AffiliationModel
                    ? affiliation
                    : new AffiliationModel(affiliation);

            this.set(attrs, {silent: true});
        },

        /**
         * Whether or not the current actor can customize this profile.
         * Note that users under 13 without parental consent can only
         * edit some data; clients should also check isDataCollectible for full
         * information about fields which can be edited.
         */
        isEditable: function (this: any): boolean {
            return this.get("isSelf") && !this.get("isPhantom");
        },

        isFullyEditable: function (this: any) {
            // TODO(csilvers): add isDataCollectible to the interface?
            return this.isEditable() && this.get("isDataCollectible");
        },

        /**
         * For giving Sal a fun profile
         */
        isSal: function (this: any) {
            return this.get("username") === "sal";
        },

        toJSON: function (this: any): Backbone_Attrs {
            // @ts-expect-error - TS2339 - Property '__super__' does not exist on type 'Class<ProfileModelType>'.
            const json = ProfileModel.__super__.toJSON.call(this);

            // Serialize computed attributes by simply calling them
            // @ts-expect-error - TS2339 - Property 'COMPUTED_ATTRS' does not exist on type 'Class<ProfileModelType>'.
            ProfileModel.COMPUTED_ATTRS.forEach((attr) => {
                json[attr] = this[attr]();
            });

            // Serialize submodel attributes by calling toJSON() on them
            // @ts-expect-error - TS2339 - Property 'SUBMODEL_ATTRS' does not exist on type 'Class<ProfileModelType>'.
            ProfileModel.SUBMODEL_ATTRS.forEach((attr) => {
                const value = this.get(attr);
                if (value.toJSON) {
                    json[attr] = value.toJSON();
                }
            });

            return json;
        },

        /**
         * Override Backbone.Model.save since only some of the fields are
         * mutable and saveable.
         */
        save: function (attrs: Backbone_Attrs, options?: any) {
            Log.error(
                "Unexpected call to ProfileMode.save",
                Errors.NotAllowed,
                {
                    sentryData: {
                        contexts: {
                            extras: {
                                // Backbone type uses mixed and so isn't
                                // compatible here.
                                // @ts-expect-error - TS2322 - Type '{ [name: string]: unknown; }' is not assignable to type 'Metadata | MetadataPrimitive | MetadataArray<Metadata | MetadataPrimitive>'.
                                attrs: {...attrs},
                                options: options || "none",
                            },
                        },
                    },
                },
            );
            Log.log(
                "DO NOT USE ProfileModel.save. You might want saveAndUpdateProfile.",
            );
        },

        // TODO: figure out how to do this in a more systematic way!
        // Override base Backbone.parse since badge modifications can result in
        // api_action_results to be sent back.
        parse: function (this: any, resp: any, xhr: XMLHttpRequest) {
            if ("apiActionResults" in resp && "payload" in resp) {
                resp = resp["payload"];
            }
            return Backbone.Model.prototype.parse.call(this, resp, xhr);
        },

        validateNickname: function (this: any, nickname: string) {
            this.trigger(
                "validate:nickname",
                (nickname || "").trim().length > 0,
            );
        },
    },
    {
        /**
         * List of attributes that are computed from other attributes. Functions
         * with these names will automatically be evaluated in/added to the
         * serialized output of toJSON().
         */
        COMPUTED_ATTRS: [
            "isEditable",
            "isFullyEditable",
            "isSal",
            "usernameFormatted",
        ],

        /**
         * List of attributes that themselves are instances of other Backbone
         * models.
         */
        SUBMODEL_ATTRS: ["affiliation"],
    },
);

export default ProfileModel;
